import { useEffect, useId, useState } from "react";
import type { AmzAttributes, AmzFieldDef } from "../../types/amazon";
import type { AmzDimensions, AmzFieldValue, AmzMeasure } from "../../lib/amazonContent";
import { attrEqual, getFieldValue } from "../../lib/amazonContent";
import { Badge } from "./AmzUi";

interface AttrFieldProps {
  def: AmzFieldDef;
  draft: AmzAttributes;
  current: AmzAttributes | null;
  isTarget: boolean;
  onChange: (value: AmzFieldValue) => void;
  onAi: () => void;
}

/** 数値入力。入力途中の文字列（"1." など）を壊さないよう、表示用の文字列を内部に持つ。 */
function NumInput({
  value,
  disabled,
  onCommit,
  placeholder,
}: {
  value: number | "" | null;
  disabled?: boolean;
  onCommit: (v: number | "") => void;
  placeholder?: string;
}) {
  const [text, setText] = useState(value === null || value === "" ? "" : String(value));
  useEffect(() => {
    const propNum = value === null ? "" : value;
    const localNum = text.trim() === "" || !Number.isFinite(Number(text)) ? "" : Number(text);
    if (propNum !== localNum) setText(propNum === "" ? "" : String(propNum));
    // text は意図的に依存へ入れない（外部から値が変わった時だけ同期する）
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return (
    <input
      className="amz-input"
      type="text"
      inputMode="decimal"
      value={text}
      disabled={disabled}
      placeholder={placeholder}
      onChange={(e) => {
        const t = e.target.value;
        setText(t);
        const n = t.trim() === "" ? "" : Number(t);
        if (n === "" || Number.isFinite(n)) onCommit(n);
      }}
    />
  );
}

function MultiInput({
  values,
  options,
  disabled,
  onChange,
}: {
  values: string[];
  options?: { value: string; label: string }[];
  disabled?: boolean;
  onChange: (v: string[]) => void;
}) {
  const [draft, setDraft] = useState("");
  const listId = useId();
  const add = () => {
    const t = draft.trim();
    if (!t || values.includes(t)) return;
    onChange([...values, t]);
    setDraft("");
  };
  return (
    <div className="amz-multi">
      {values.length > 0 && (
        <div className="amz-multi-chips">
          {values.map((v, i) => (
            <span key={`${v}-${i}`} className="amz-multi-chip">
              {v}
              {!disabled && (
                <button type="button" aria-label={`${v} を削除`} onClick={() => onChange(values.filter((_, j) => j !== i))}>
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {!disabled && (
        <div className="amz-multi-add">
          <input
            className="amz-input"
            list={options ? listId : undefined}
            value={draft}
            placeholder="追加（Enter）"
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                e.preventDefault();
                add();
              }
            }}
          />
          <button type="button" className="amz-btn sm" onClick={add} disabled={!draft.trim()}>
            追加
          </button>
          {options && (
            <datalist id={listId}>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </datalist>
          )}
        </div>
      )}
    </div>
  );
}

function jsonText(draft: AmzAttributes, key: string): string {
  const list = (draft[key] ?? []) as Record<string, unknown>[];
  if (list.length === 0) return "（未設定）";
  const clean = list.map((e) => {
    const c = { ...e };
    delete c.marketplace_id;
    delete c.language_tag;
    return c;
  });
  return JSON.stringify(clean.length === 1 ? clean[0] : clean, null, 1);
}

export function AttrField({ def, draft, current, isTarget, onChange, onAi }: AttrFieldProps) {
  const disabled = !def.editable;
  const changed = current !== null && !attrEqual(current[def.key], draft[def.key]);
  const value = getFieldValue(draft, def.key, def);
  const listId = useId();

  let input: JSX.Element;
  switch (def.type) {
    case "text":
    case "textarea":
      if (def.multi) {
        input = <MultiInput values={(value as string[]) ?? []} disabled={disabled} onChange={onChange} />;
      } else if (def.type === "textarea") {
        input = (
          <textarea
            className="amz-textarea"
            rows={3}
            value={(value as string) ?? ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      } else {
        input = (
          <input className="amz-input" value={(value as string) ?? ""} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
        );
      }
      break;
    case "number":
      input = <NumInput value={(value as number | null) ?? null} disabled={disabled} onCommit={(n) => onChange(n === "" ? null : n)} />;
      break;
    case "boolean":
      input = (
        <select
          className="amz-select"
          value={value === true ? "true" : value === false ? "false" : ""}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "true")}
        >
          <option value="">（未設定）</option>
          <option value="true">はい</option>
          <option value="false">いいえ</option>
        </select>
      );
      break;
    case "enum":
      if (def.multi) {
        input = <MultiInput values={(value as string[]) ?? []} options={def.options} disabled={disabled} onChange={onChange} />;
      } else if (def.freeEnum) {
        input = (
          <>
            <input
              className="amz-input"
              list={listId}
              value={(value as string) ?? ""}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
            />
            <datalist id={listId}>
              {(def.options ?? []).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </datalist>
          </>
        );
      } else {
        const v = (value as string) ?? "";
        const known = (def.options ?? []).some((o) => o.value === v);
        input = (
          <select className="amz-select" value={v} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
            <option value="">（未設定）</option>
            {v !== "" && !known && <option value={v}>{v}</option>}
            {(def.options ?? []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        );
      }
      break;
    case "measure": {
      const m = (value as AmzMeasure | null) ?? { value: "", unit: def.units?.[0]?.value ?? "" };
      const unit = m.unit || def.units?.[0]?.value || "";
      input = (
        <div className="amz-measure">
          <NumInput value={m.value} disabled={disabled} onCommit={(n) => onChange({ value: n, unit })} />
          <select className="amz-select" value={unit} disabled={disabled} onChange={(e) => onChange({ value: m.value, unit: e.target.value })}>
            {(def.units ?? [{ value: unit, label: unit }]).map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </div>
      );
      break;
    }
    case "dimensions": {
      const empty: AmzMeasure = { value: "", unit: def.units?.[0]?.value ?? "" };
      const d = (value as AmzDimensions | null) ?? { length: empty, width: empty, height: empty };
      const unit = d.length.unit || d.width.unit || d.height.unit || def.units?.[0]?.value || "";
      const part = (k: "length" | "width" | "height", label: string) => (
        <NumInput
          key={k}
          value={d[k].value}
          disabled={disabled}
          placeholder={label}
          onCommit={(n) => onChange({ ...d, length: { ...d.length, unit }, width: { ...d.width, unit }, height: { ...d.height, unit }, [k]: { value: n, unit } })}
        />
      );
      input = (
        <div className="amz-dims">
          {part("length", "縦")}
          {part("width", "横")}
          {part("height", "高さ")}
          <select
            className="amz-select"
            value={unit}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                length: { ...d.length, unit: e.target.value },
                width: { ...d.width, unit: e.target.value },
                height: { ...d.height, unit: e.target.value },
              })
            }
          >
            {(def.units ?? [{ value: unit, label: unit }]).map((u) => (
              <option key={u.value} value={u.value}>
                {u.label}
              </option>
            ))}
          </select>
        </div>
      );
      break;
    }
    case "json":
    default:
      input = <pre className="amz-json">{jsonText(draft, def.key)}</pre>;
  }

  const wide = def.type === "dimensions" || def.multi || def.type === "json" || def.type === "textarea";

  return (
    <div className={`amz-attr ${wide ? "wide" : ""}`} data-target={isTarget ? "1" : undefined}>
      <div className="amz-attr-head">
        <span className="amz-attr-label">
          {def.label} <span className="amz-attr-key">{def.key}</span>
          {def.required && <span className="amz-req"> *</span>}
        </span>
        <span className="amz-attr-flags">
          {changed && (
            <Badge tone="accent" size="xs">
              変更
            </Badge>
          )}
          {def.lockNote && <span className="amz-lock-note">{def.lockNote}</span>}
          {def.aiAllowed && def.editable && (
            <button type="button" className="amz-ai-pill sm" onClick={onAi}>
              AI
            </button>
          )}
        </span>
      </div>
      {input}
    </div>
  );
}
